import { AGENT_TYPES, UploadDialogState } from "@/modules/agent/types";
import UploadDialog from "@/modules/agent/components/upload-dialog";
import { BreachHistoryColumns } from "@/modules/agent/components/TPRADashboard/constants/breachHistoryColumns";
import { useServerSideState } from "@/modules/agent/components/TPRADashboard/hooks/useServerSideState";
import { useGetBreachHistory } from "@/modules/agent/components/TPRADashboard/requests/getBreachHistory";
import { BreachHistoryItem } from "@/modules/agent/components/TPRADashboard/types/breachHistory";
import { ZButton } from "@/shared/components/button";
import ZBreadCrumbs from "@/shared/components/custom/breadcrumbs";
import { LoadingSpinner } from "@/shared/components/custom/spin";
import ErrorBoundary from "@/shared/components/error-boundary";
import { ZTableDeprecrated } from "@/shared/components/table-deprecrated/TableDeprecrated";
import { ZTablePagination } from "@/shared/components/TablePagination";
import { SearchField } from "@/shared/components/TableSearch";
import { ROUTES } from "@/shared/constants/routes";
import { cn } from "@nextui-org/react";
import { useState } from "react";

export default function TPRABreachHistoryPage() {
  // Modal state
  const [uploadDialogState, setUploadDialogState] = useState<UploadDialogState>({
    isOpen: false,
  });

  // Server-side state management (pagination, search)
  const { queryParams, pagination, search } = useServerSideState({
    page: 1,
    page_size: 25,
  });

  // Fetch breach history data from API
  const { data, isLoading, isFetching, isError, error } = useGetBreachHistory({
    ...queryParams,
  });

  // Extract data from API response
  const breaches = data?.breaches ?? [];
  const totalBreaches = data?.total_breaches ?? 0;
  const totalPages = data?.total_pages ?? 0;
  const currentPage = data?.page ?? 1;

  // Handle opening the modal
  const handleNewAssessment = () => {
    setUploadDialogState({
      isOpen: true,
      type: AGENT_TYPES.MULTI_FILE_VENDOR_ASSESSMENT,
    });
  };

  return (
    <ErrorBoundary fallback={null}>
      <div className="w-full h-full flex flex-col">
        {/* Top Bar: Breadcrumb + New Assessment Button */}
        <div className="flex items-center pb-4 justify-between min-h-[44px] max-h-[44px]">
          <div className="flex gap-4 items-center">
            <ZBreadCrumbs
              items={[
                { title: "Third-Party Risk", href: `/${ROUTES.THIRD_PARTY_HOME}` },
                { title: "Breach History", href: `/${ROUTES.THIRD_PARTY_BREACH_HISTORY}` },
              ]}
            />
          </div>
          <ZButton
            color="primary"
            className="text-xs font-medium h-7 px-3"
            onClick={handleNewAssessment}
          >
            New Assessment
          </ZButton>
        </div>

        {/* Main Content */}
        <div className="h-full w-full overflow-hidden rounded-lg flex flex-col">
          {/* Header: Title + Count | Search */}
          <div className="px-6 py-4 flex flex-col bg-white gap-2">
            <div className="flex flex-row justify-between items-center gap-2 flex-shrink-0">
              {/* Left: Title + Count */}
              <div className="flex flex-row items-center gap-2 flex-grow">
                <h2 className="font-['Inter'] font-semibold text-base leading-[20px] text-title-primary">
                  Breach History
                </h2>
                {!isLoading && !isFetching && (
                  <div className="flex flex-row justify-center items-center py-[1px] px-2 gap-1 bg-badge-bg rounded-[45px]">
                    <span className="font-['Inter'] font-medium text-[10px] leading-[16px] text-badge-text">
                      {totalBreaches}
                    </span>
                  </div>
                )}
              </div>

              {/* Right: Search */}
              <div className="flex flex-row items-center gap-2">
                <SearchField
                  value={search.searchTerm}
                  onChange={search.setSearchTerm}
                  placeholder="Search by name"
                  className="w-[420px]"
                />
              </div>
            </div>
          </div>

          {/* Table */}
          <div className="flex-1 flex flex-col overflow-hidden">
            <div className="relative flex-1 min-h-0">
              {/* Loading overlay - only during refetch, not initial load */}
              {!isLoading && isFetching && (
                <div className="absolute inset-0 bg-white/60 z-10 flex items-center justify-center rounded-lg">
                  <LoadingSpinner />
                </div>
              )}
              <ZTableDeprecrated<BreachHistoryItem>
                className={cn(
                  "border-1 border-[#E4E4E7] overflow-auto scroll-auto h-full"
                )}
                layout="auto"
                loading={isLoading || isFetching}
                rows={breaches}
                columns={BreachHistoryColumns}
                isHeaderSticky
                isCompact
                removeWrapper
                getRowId={(row) => row.id}
                tableHeaderProps={{ className: "shadow-none rounded-none" }}
                tableRowProps={(row, rowIndex) => ({
                  className: cn(rowIndex === 0 ? "border-none" : "border-y", "bg-white hover:bg-table-row-hover", "group"),
                })}
                classNames={{
                  wrapper: "!shadow-none !rounded-none bg-white",
                  base: "!shadow-none !rounded-none bg-white",
                  tbody: "bg-white",
                  tr: "bg-white",
                  th: "!shadow-none !rounded-none !bg-table-header-bg text-table-header-text font-semibold text-xs",
                  td: "text-table-row-text",
                }}
                aria-label="Breach History List"
                tableBodyProps={{
                  emptyContent: isError ? (
                    <div className="flex flex-col items-center justify-center py-8 gap-2">
                      <p className="text-sm text-gray-500">Failed to load breach history</p>
                      {error && <p className="text-xs text-red-500">{error.message}</p>}
                    </div>
                  ) : (
                    <div className="flex items-center justify-center py-8">
                      {isLoading || isFetching ? (
                        <LoadingSpinner containerClassName="bg-transparent relative" className="mx-auto" />
                      ) : (
                        <p className="text-sm text-gray-500">No breaches found</p>
                      )}
                    </div>
                  ),
                }}
              />
            </div>
            <ZTablePagination
              totalPages={totalPages}
              paginationState={{
                currentPage: currentPage,
                rowsPerPage: pagination.pageSize,
              }}
              setPagination={{
                handlePageChange: (page) => pagination.setPage(page),
                handleRowsPerPageChange: (rows) => pagination.setPageSize(rows),
              }}
              className="px-5 !py-3 border-t border-[#E4E4E7]"
            />
          </div>
        </div>
      </div>

      {/* Upload Dialog */}
      <UploadDialog data={uploadDialogState} setIsOpen={setUploadDialogState} />
    </ErrorBoundary>
  );
}
